import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { GpoOrderRow } from './types';

const endpoint = 'ums/v1/cms/reconciliation/gpo-orders';
export const getGpoOrdersEndpointIdentifier = endpoint;

interface ResponseData {
  data: GpoOrderRow[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  { page: TParam; limit: TParam; supplierId: TParam; restaurantId: TParam; dateFrom: TParam; dateTo: TParam; sort: TParam }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetGpoOrdersApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { supplierId, restaurantId, dateFrom, dateTo, sort } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: { page, limit, supplierId, restaurantId, dateFrom, dateTo, sort },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, supplierId, restaurantId, dateFrom, dateTo, sort }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
